import {
  Component,
  inject,
  input,
  OnInit,
  output,
  viewChild,
  viewChildren
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule } from '@angular/forms';

import {
  NgbAccordionBody,
  NgbAccordionCollapse,
  NgbAccordionDirective,
  NgbAccordionHeader,
  NgbAccordionItem,
  NgbTooltip
} from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin } from 'rxjs';
import {
  addDate,
  BaseStep,
  ButtonComponent,
  CheckboxComponent,
  FormFieldComponent,
  hasAccordionOpenPanels,
  isSalesProperty,
  minDateValidator,
  ModalService,
  SearchProfileDetailsComponent,
  SearchProfileInfoComponent,
  searchProfileNotificationConfig,
  SearchProfileService
} from '@ui/legacy-lib';
import {
  CityPacket,
  Constants,
  ContactsDeletionConfig,
  InternationalizationSettings,
  PropertyType,
  RegistrationLinkObjectTypes,
  SearchProfile
} from '@ui/shared/models';
import { TranslateModule } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'app-internal-list-member-search-profile',
  templateUrl: './member-search-profile.component.html',
  styleUrls: ['./member-search-profile.component.scss'],
  providers: [SearchProfileService],
  imports: [
    NgbAccordionDirective,
    NgbAccordionItem,
    NgbAccordionHeader,
    NgbAccordionCollapse,
    NgbAccordionBody,
    FormsModule,
    NgbTooltip,
    TranslateModule,
    FormFieldComponent,
    ButtonComponent,
    CheckboxComponent,
    SearchProfileInfoComponent,
    SearchProfileDetailsComponent
  ]
})
export class MemberSearchProfileComponent extends BaseStep implements OnInit {
  private modalService = inject(ModalService);
  private fb = inject(FormBuilder);
  private searchProfileService = inject(SearchProfileService);

  readonly formArray = input<FormArray>(undefined);
  readonly existingSearchProfilesAccordion = viewChild<NgbAccordionDirective>(
    'existingSearchProfiles'
  );
  readonly accordionItems = viewChildren(NgbAccordionItem);
  readonly disableActions = input<boolean>(undefined);
  readonly sensitiveDataAccessible = input<boolean>(undefined);
  readonly inviteToRegister = input<boolean>(undefined);
  readonly isOfflineUser = input<boolean>(undefined);
  readonly isManuallyCreated = input<boolean>(undefined);
  readonly isSelfRegistration = input<boolean>(undefined);
  readonly isNewContact = input<boolean>(undefined);
  readonly agentNotAllowedToCreateSP = input<boolean>(undefined);
  readonly constants = input<Constants>(undefined);
  readonly contactsDeletionConfig = input<ContactsDeletionConfig>(undefined);
  readonly showInviteToRegister = input<boolean>(undefined);
  readonly showMarketingType = input<boolean>(undefined);
  readonly internalizationSettings =
    input<InternationalizationSettings>(undefined);
  readonly landlordCityPacket = input<CityPacket>(undefined);
  readonly onlyShowConfiguredCityPartsToUser = input(false);
  readonly isRegistration = input(false);
  readonly defaultObjectTypes = input<RegistrationLinkObjectTypes>(undefined);
  readonly disableActionsChange = output<boolean>();
  readonly sensitiveDataAccessibleChange = output<boolean>();
  readonly inviteToRegisterChange = output<boolean>();

  public searchProfilesInfoPayload: SearchProfile[] = [];
  public hasSalesSearchProfile: boolean;

  public get hasOpenPanels() {
    return hasAccordionOpenPanels(this.accordionItems());
  }

  private get searchProfileFormGroup() {
    const group = this.searchProfileService.searchProfileForm;

    const defaultObjectTypes = this.defaultObjectTypes();
    if (
      defaultObjectTypes &&
      !defaultObjectTypes?.propertyTypes?.includes(
        group.controls.propertyType.value as PropertyType
      )
    )
      group.controls.propertyType.patchValue(null);

    const validators = this.isManuallyCreated()
      ? this.resolveSearchProfileEndDateValidators(
          this.contactsDeletionConfig()?.landlordOwned
            ?.contactsSearchProfileEndDateAdjustable,
          this.contactsDeletionConfig()?.landlordOwned
            ?.contactsDeletionUnitAmount,
          this.contactsDeletionConfig()?.landlordOwned?.contactsDeletionUnit
        )
      : this.resolveSearchProfileEndDateValidators(
          this.contactsDeletionConfig()?.userOwned
            ?.contactsSearchProfileEndDateAdjustable,
          this.contactsDeletionConfig()?.userOwned?.contactsDeletionUnitAmount,
          this.contactsDeletionConfig()?.userOwned?.contactsDeletionUnit
        );

    this.isManuallyCreated()
      ? this.resolveSearchProfileEndDateControl(
          group,
          this.contactsDeletionConfig()?.landlordOwned?.contactsDeletionActive,
          this.contactsDeletionConfig()?.landlordOwned
            ?.contactsDeletionUnitAmount,
          this.contactsDeletionConfig()?.landlordOwned?.contactsDeletionUnit,
          this.contactsDeletionConfig()?.landlordOwned
            ?.contactsSearchProfileEndDateAdjustable,
          validators
        )
      : this.resolveSearchProfileEndDateControl(
          group,
          this.contactsDeletionConfig()?.userOwned?.contactsDeletionActive,
          this.contactsDeletionConfig()?.userOwned?.contactsDeletionUnitAmount,
          this.contactsDeletionConfig()?.userOwned?.contactsDeletionUnit,
          this.contactsDeletionConfig()?.userOwned
            ?.contactsSearchProfileEndDateAdjustable,
          validators
        );

    return group;
  }

  ngOnInit() {
    this.getSearchProfileData();
    this.formArray()
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((formArray: SearchProfile[]) => {
        this.hasSalesSearchProfile = formArray.some(sp =>
          isSalesProperty(sp.marketingType)
        );
        if (this.hasSalesSearchProfile) this.onInviteToRegisterChange(false);
      });

    super.ngOnInit();
  }

  public isPanelOpen(itemId: string) {
    return this.existingSearchProfilesAccordion().isExpanded(itemId);
  }

  public updateSearchProfile(index: number) {
    // Patching randomId to differentiate between valid and invalid SPs
    // inside the formArray. So, we edit a SP which has an id and then
    // we cancel the process, that SP does not get removed from the formArray
    const idControl = this.formArray().at(index).get('id');
    if (!idControl.getRawValue()) idControl.patchValue('randomId');
    this.existingSearchProfilesAccordion().collapse(
      `search-profile-panel-${index}`
    );
    this.disableActionsChange.emit(false);
    this.getSearchProfileData(index);
  }

  public onDeleteSearchProfile(index: number) {
    const options = {
      data: searchProfileNotificationConfig.searchProfile.delete
    };

    this.modalService
      .openConfirmation(options)
      .onClose()
      .subscribe(() => {
        this.formArray().removeAt(index);
        this.getSearchProfileData();
      });
  }

  public toggleSearchProfile(id: number) {
    this.existingSearchProfilesAccordion().toggle(`search-profile-panel-${id}`);
    this.disableActionsChange.emit(!this.disableActions());
  }

  public cancelEdit(index: number) {
    this.toggleSearchProfile(index);

    // Whenever we click 'add search profile', we add a new formGroup in the formArray.
    // If we do not fill the data for the formGroup and click on 'cancel', then the formArray
    // is invalid and the formGroup is empty. On the other side, when we save a formGroup,
    // we assign randomId to it (updateSearchProfile method). So, that way we can check
    // if the formGroup has no id, then means it was never 'saved' and we remove it from formArray
    if (!this.formArray().at(index).get('id').getRawValue()) {
      this.formArray().removeAt(index);
    }
  }

  public newSearchProfile() {
    this.disableActionsChange.emit(true);
    this.formArray().push(this.searchProfileFormGroup);
    setTimeout(() =>
      this.existingSearchProfilesAccordion().expand(
        `search-profile-panel-${this.formArray().length - 1}`
      )
    );
  }

  public onSensitiveDataAccessible(value: boolean) {
    this.sensitiveDataAccessibleChange.emit(value);
  }

  public onInviteToRegisterChange(value: boolean) {
    this.inviteToRegisterChange.emit(value);
  }

  public getSearchProfileData(index?: number) {
    if (!index) {
      const searchProfiles$ = this.formArray().controls.map(control =>
        this.searchProfileService.getSearchProfileInfoPayload(
          control.getRawValue()
        )
      );
      forkJoin(searchProfiles$).subscribe(
        searchProfile => (this.searchProfilesInfoPayload = searchProfile)
      );
    } else {
      this.searchProfileService
        .getSearchProfileInfoPayload(this.formArray().at(index).getRawValue())
        .subscribe(
          searchProfile =>
            (this.searchProfilesInfoPayload[index] = searchProfile)
        );
    }
  }

  public resolveSearchProfileEndDateValidators(
    endDateAdjustable: boolean,
    amount: number,
    unit: string
  ) {
    return endDateAdjustable
      ? [minDateValidator(new Date(addDate(amount, unit)))]
      : [];
  }

  public resolveSearchProfileEndDateControl(
    group: FormGroup,
    active: boolean,
    amount: number,
    unit: string,
    endDateAdjustable: boolean,
    validators: any
  ) {
    group.setControl(
      'durationEndDate',
      this.fb.control(
        {
          value: active ? addDate(amount, unit) : null,
          disabled: active && !endDateAdjustable
        },
        {
          validators
        }
      )
    );
  }
}
